import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { isOnline } from "src/utils";
import {
  setEditing,
  setEntryLockData,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";

/**
 * Custom hook to manage entry locks for logbook entries.
 */
export const useEntryLock = () => {
  const { post } = usePostApi();
  const { idType } = useParams();
  const locked24 = useSelector(state => state.generatorSlice.locked24);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const {
    current,
    editing,
    activeAutosave,
    releaseEntryLock,
    locked,
    you,
    openedRefetchModal,
  } = useSelector(state => state.entrySlice);

  const isNewEntry = useIsNewEntry();
  const dispatch = useDispatch();
  const idObject = useMemo(
    () => ({ [String(idType)]: isNewEntry ? "new" : id }),
    [idType, isNewEntry, id],
  );

  const lastExistingIdObjectRef = useRef<Record<string, string> | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveDelayRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckLockTimeRef = useRef<number>(0);
  const prevEditingRef = useRef(editing);
  const releaseInFlightRef = useRef(false);
  const openedRefetchModalRef = useRef(openedRefetchModal);

  useEffect(() => {
    if (
      !isNewEntry &&
      idObject[String(idType)] &&
      idObject[String(idType)] !== "new"
    ) {
      lastExistingIdObjectRef.current = { ...idObject };
    }
  }, [idObject, idType, isNewEntry]);

  useEffect(() => {
    openedRefetchModalRef.current = openedRefetchModal;
  }, [openedRefetchModal]);

  const clearKeepAliveIntervals = useCallback(() => {
    if (keepAliveDelayRef.current) {
      clearTimeout(keepAliveDelayRef.current);
      keepAliveDelayRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const handleCheckLock = useCallback(() => {
    if (!isOnline()) return;
    void post({
      request: "check-entry-lock",
      body: { params: { ...idObject, current } },
      onSuccess: data =>
        dispatch(
          setEntryLockData({
            ...data,
            activeAutosave: false,
          }),
        ),
    });
  }, [current, dispatch, idObject, post]);

  const handleGetLock = useCallback(() => {
    if (!isOnline()) return;

    if (Object.values(idObject)[0] !== "new") {
      void post({
        request: "get-entry-lock",
        body: { params: { ...idObject, current } },
        onSuccess: data => {
          dispatch(
            setEntryLockData({
              ...data,
              activeAutosave: data.locked && data.you,
              lockedByOther: data.locked && !data.you,
            }),
          );
        },
      });
    } else {
      dispatch(
        setEntryLockData({
          locked: true,
          user: null,
          you: true,
          changed: 0,
          activeAutosave: true,
        }),
      );
    }
  }, [current, dispatch, idObject, post]);

  const handleKeepAlive = useCallback(() => {
    if (!isOnline()) return;
    void post({
      request: "keep-alive-entry-lock",
      body: { params: { ...idObject } },
      onError: () => {
        if (releaseInFlightRef.current || !editing) return;
        void handleGetLock();
      },
    });
  }, [editing, idObject, post, handleGetLock]);

  const handleReleaseEntryLock = useCallback(async () => {
    if (!isOnline()) return;

    releaseInFlightRef.current = true;
    clearKeepAliveIntervals();

    const releaseParams = !isNewEntry
      ? idObject
      : lastExistingIdObjectRef.current;

    if (releaseParams) {
      await post({
        request: "release-entry-lock",
        body: { params: { ...releaseParams } },
      });
    }

    dispatch(
      setEntryLockData({
        locked: false,
        you: false,
        user: null,
        activeAutosave: false,
      }),
    );
    releaseInFlightRef.current = false;
  }, [clearKeepAliveIntervals, dispatch, idObject, isNewEntry, post]);

  const releaseLock = useCallback(() => {
    dispatch(setReleaseEntryLock(0));
    void handleReleaseEntryLock();
  }, [dispatch, handleReleaseEntryLock]);

  useEffect(() => {
    let checkLockInterval: NodeJS.Timeout | undefined;

    if (locked24) {
      prevEditingRef.current = editing;
      return;
    }

    if (locked && !you) {
      dispatch(setEditing(false));
    }

    const currentEntryId = Object.values(idObject)[0];
    const canCheckLock =
      Boolean(current) && Boolean(currentEntryId) && currentEntryId !== "new";

    const prevEditing = prevEditingRef.current;
    const editingStarted = editing && !prevEditing;

    if (!editing && (!locked || (locked && !you))) {
      if (canCheckLock) {
        const now = Date.now();
        if (now - lastCheckLockTimeRef.current > 2500) {
          lastCheckLockTimeRef.current = now;
          void handleCheckLock();
        }
        checkLockInterval = setInterval(() => {
          lastCheckLockTimeRef.current = Date.now();
          void handleCheckLock();
        }, 3000);
      }
    }

    if (editing) {
      if (editingStarted && !locked) {
        void handleGetLock();
      }

      if (activeAutosave && locked && you) {
        if (!keepAliveDelayRef.current && !keepAliveIntervalRef.current) {
          keepAliveDelayRef.current = setTimeout(() => {
            keepAliveDelayRef.current = null;
            if (!openedRefetchModalRef.current) {
              void handleKeepAlive();
            }

            keepAliveIntervalRef.current = setInterval(() => {
              if (!openedRefetchModalRef.current) {
                if (!window.location.href.includes("logbook")) {
                  clearKeepAliveIntervals();
                  void handleReleaseEntryLock();
                  return;
                }
                void handleKeepAlive();
              }
            }, 5000);
          }, 5000);
        }
      }
    } else {
      clearKeepAliveIntervals();
    }

    prevEditingRef.current = editing;

    return () => {
      if (checkLockInterval) clearInterval(checkLockInterval);
    };
  }, [
    activeAutosave,
    clearKeepAliveIntervals,
    current,
    dispatch,
    editing,
    handleCheckLock,
    handleGetLock,
    handleKeepAlive,
    handleReleaseEntryLock,
    idObject,
    locked,
    locked24,
    you,
  ]);

  useEffect(() => {
    if (releaseEntryLock !== 0) {
      releaseLock();
    }
  }, [releaseEntryLock, releaseLock]);

  useEffect(() => {
    return () => {
      clearKeepAliveIntervals();
      if (!window.location.href.includes("logbook")) {
        void handleReleaseEntryLock();
      }
    };
  }, [clearKeepAliveIntervals, handleReleaseEntryLock]);
};
